/**
 * Event content for a self-schedule picked slot. Time label top-left
 * (ellipsised if the pill is narrow) with a × pinned to the
 * top-right so it is visible regardless of slot height or label
 * width.
 */
import { Box, Flex, IconButton, Tooltip } from '@chakra-ui/react';
import { CloseIcon } from '@chakra-ui/icons';
import moment from 'moment-timezone';
import { KText } from '@/ui-library/components/text';
import type { SlotOverrideType } from '../types';

interface SlotPillProps {
  slotId: string;
  startIso: string;
  endIso: string;
  timezone: string;
  /** Origin — forwarded to the reducer so it knows whether to stash
   *  the id in `droppedIds` or drop from `customPicks`. */
  isGenerated: boolean;
  /** "Send additional slots" flow — candidate has already booked this
   *  slot. Hides the × and applies a confirmed-style label. */
  isBooked?: boolean;
  /**
   * Backend override classification (`is_custom`/`override_type`).
   * When non-`'none'` we wrap the pill in a "Custom slot" tooltip so
   * the recruiter sees the slot is overriding a constraint (busy
   * event, working hours, or both). Mirrors legacy
   * `SelfFreeSlot.tsx`'s tooltip behaviour. Currently set in two
   * places: `seedFromInitial` (server-sourced slots) and
   * `payload/computeSlotOverride.ts` at save time — but the latter
   * runs only when the user clicks "Schedule", so live drag/click
   * picks won't show this tooltip until they're persisted then
   * re-loaded. That's a UX gap to revisit if recruiters ask for a
   * live indicator. See `docs/SELF_SCHEDULE_BEHAVIOR.md`.
   */
  overrideType?: SlotOverrideType;
  onRemove: (_slotId: string, _isGenerated: boolean) => void;
}

export function SlotPill({
  slotId,
  startIso,
  endIso,
  timezone,
  isGenerated,
  isBooked = false,
  overrideType,
  onRemove,
}: SlotPillProps) {
  const label = moment.tz(startIso, timezone).format('hh:mm A');
  const endLabel = moment.tz(endIso, timezone).format('hh:mm A');
  const isOverride = Boolean(overrideType && overrideType !== 'none');

  const content = (
    <Box
      position="relative"
      h="100%"
      w="100%"
      px="6px"
      pt="4px"
      overflow="hidden"
    >
      {/* Label sits flush-left; right padding reserves space so the
          × never sits on top of the text. Ellipsis kicks in when the
          pill is narrower than the formatted time. */}
      <Flex align="flex-start" pr={isBooked ? '6px' : '18px'}>
        <KText
          fontSize="11px"
          fontWeight="600"
          whiteSpace="nowrap"
          overflow="hidden"
          textOverflow="ellipsis"
          lineHeight="16px"
          w="100%"
          color={isBooked ? 'success-700' : undefined}
        >
          {isBooked ? `${label} • Booked` : label}
        </KText>
      </Flex>
      {isBooked ? null : (
        <IconButton
          aria-label={`Remove slot ${label}–${endLabel}`}
          icon={<CloseIcon boxSize="7px" />}
          size="xs"
          variant="ghost"
          minW="14px"
          h="14px"
          p={0}
          position="absolute"
          top="4px"
          right="4px"
          color="primary-700"
          _hover={{ bg: 'primary-100' }}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            onRemove(slotId, isGenerated);
          }}
        />
      )}
    </Box>
  );

  return isOverride ? (
    <Tooltip label="Custom slot" placement="top" hasArrow>
      {content}
    </Tooltip>
  ) : (
    content
  );
}
